import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Card, Button, Badge, AsyncBoundary, Drawer, useToast, Notice,
} from '@kollez/design-system';
import {
  useExamResults,
  useReviewAnswer,
  useAttempt,
  useAssessmentAggregateResults,
  type ResultRow,
} from '@/lib/queries';

/**
 * /assess/exams/:examId/results (F68) — attempt roster with pass/fail, integrity summary, and
 * per-attempt review drawer. Faculty/TPO with `assessment:review_answer` can score pending
 * answers here.
 */
export function AssessResultsPage() {
  const { examId = '' } = useParams();
  const { data, isLoading, error, refetch } = useExamResults(examId);
  // Part 3 — aggregate strip: histogram + per-question stats. Separate query so the roster
  // still renders even if the aggregate endpoint 500s.
  const aggregate = useAssessmentAggregateResults(examId);
  const [drawerAttemptId, setDrawerAttemptId] = useState<string | null>(null);

  return (
    <AsyncBoundary kind="table" loading={isLoading} error={error} empty={!isLoading && !data}
      emptyTitle="No attempts yet" emptyHint="Once candidates submit, results land here."
      onRetry={() => void refetch()}>
      {data && (
        <div className="stack">
          <div className="page-header">
            <div className="ph-text">
              <p className="eyebrow">Results · {data.assessment.name}</p>
              <h1 className="h-page">Attempts</h1>
              <p className="subhead">{data.assessment.totalMarks} marks total{data.assessment.passingMarks ? ` · passing ${data.assessment.passingMarks}` : ''}</p>
            </div>
            <div className="ph-actions">
              <Link to={`/assess/exams`}><Button variant="ghost">← All assessments</Button></Link>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <Kpi label="Attempts" value={data.summary.totalAttempts} />
            <Kpi label="Submitted" value={data.summary.submitted} />
            <Kpi label="Under review" value={data.summary.underReview} tone={data.summary.underReview > 0 ? 'info' : undefined} />
            <Kpi label="Passed" value={data.summary.passed} tone="success" />
            <Kpi label="Failed" value={data.summary.failed} tone="warning" />
            <Kpi label="Flagged" value={data.summary.flagged} tone={data.summary.flagged > 0 ? 'warning' : undefined} />
          </div>

          {/* Part 3 — aggregate strip: score distribution + per-question. Renders empty-state when
              no attempts yet, so the page is meaningful before candidates submit. */}
          {aggregate.data && <AggregateBlock data={aggregate.data} />}

          <Card padding="md">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-ink-tertiary">
                <tr>
                  <th className="text-left py-2">Candidate</th>
                  <th className="text-left">Status</th>
                  <th className="text-right">Score</th>
                  <th className="text-right">Pending</th>
                  <th className="text-right">Flags</th>
                  <th className="text-left">Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => (
                  <tr key={r._id} className="border-t border-line-primary">
                    <td className="py-2">
                      <div className="font-medium">{r.student.fullName ?? 'Unnamed'}</div>
                      <div className="text-xs text-ink-tertiary">{r.student.email}</div>
                    </td>
                    <td>
                      <Badge tone={r.status === 'submitted' ? 'success' : r.status === 'under_review' ? 'info' : 'outline'}>
                        {r.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="text-right font-mono">
                      {r.totalScore}{r.passed === true && <span className="ml-1 text-ink-success">✓</span>}{r.passed === false && <span className="ml-1 text-ink-warning">✗</span>}
                    </td>
                    <td className="text-right">{r.pendingReview > 0 ? <Badge tone="info">{r.pendingReview}</Badge> : '—'}</td>
                    <td className="text-right">{r.integrityFlags > 0 ? <Badge tone="warning">{r.integrityFlags}</Badge> : '—'}</td>
                    <td className="text-xs text-ink-tertiary">{r.submittedAt ? new Date(r.submittedAt).toLocaleString('en-IN') : '—'}</td>
                    <td className="text-right">
                      <Button variant="ghost" onClick={() => setDrawerAttemptId(r._id)}>Review</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {drawerAttemptId && (
            <ReviewDrawer attemptId={drawerAttemptId} onClose={() => setDrawerAttemptId(null)} />
          )}
        </div>
      )}
    </AsyncBoundary>
  );
}

function AggregateBlock({ data }: { data: import('@/lib/queries').AssessmentAggregateResponse }) {
  if (data.attemptCount === 0) {
    return (
      <Card padding="md">
        <div className="text-sm text-ink-tertiary">
          No attempts yet. Once candidates submit, aggregate results appear here — score
          distribution and per-question stats.
        </div>
      </Card>
    );
  }
  const maxCount = Math.max(1, ...data.scoreDistribution.map((b) => b.count));
  return (
    <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
      <Card padding="md">
        <div className="text-xs uppercase tracking-wide text-ink-tertiary mb-2">Score distribution</div>
        <div className="stack">
          {data.scoreDistribution.map((b) => (
            <div key={b.bucket} className="grid grid-cols-[64px_1fr_36px] items-center gap-2 text-sm">
              <span className="text-ink-tertiary">{b.bucket}</span>
              <div className="bar">
                <span style={{ width: `${(b.count / maxCount) * 100}%` }} />
              </div>
              <span className="text-right font-mono">{b.count}</span>
            </div>
          ))}
        </div>
      </Card>
      <Card padding="md">
        <div className="text-xs uppercase tracking-wide text-ink-tertiary mb-2">
          Per-question ({data.perQuestion.length})
        </div>
        {data.perQuestion.length === 0 ? (
          <div className="text-sm text-ink-tertiary">No graded answers yet.</div>
        ) : (
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-ink-tertiary">
                <tr>
                  <th className="text-left py-2">Question</th>
                  <th className="text-right">Shown</th>
                  <th className="text-right">% correct</th>
                </tr>
              </thead>
              <tbody>
                {data.perQuestion.map((q) => (
                  <tr key={q.questionId} className="border-t border-line-primary">
                    <td className="py-2">
                      <span className="line-clamp-2">{q.stem || `Question · ${q.questionId.slice(-6)}`}</span>
                    </td>
                    <td className="text-right font-mono">{q.timesShown}</td>
                    <td className="text-right font-mono">{q.correctPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: 'success' | 'warning' | 'info' }) {
  const cls = tone === 'success' ? 'text-ink-success' : tone === 'warning' ? 'text-ink-warning' : tone === 'info' ? 'text-ink-info' : 'text-ink-primary';
  return (
    <Card padding="sm">
      <div className="text-xs uppercase tracking-wide text-ink-tertiary">{label}</div>
      <div className={`font-serif text-2xl ${cls}`}>{value}</div>
    </Card>
  );
}

function ReviewDrawer({ attemptId, onClose }: { attemptId: string; onClose: () => void }) {
  const attempt = useAttempt(attemptId);
  const review = useReviewAnswer(attemptId);
  const toast = useToast();
  const [scores, setScores] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});

  async function submitScore(questionId: string) {
    const score = Number(scores[questionId] ?? '');
    const reason = reasons[questionId] ?? '';
    if (Number.isNaN(score) || score < 0) { toast.show({ tone: 'warning', message: 'Enter a valid score.' }); return; }
    if (reason.trim().length < 5) { toast.show({ tone: 'warning', message: 'Reason must be at least 5 characters.' }); return; }
    try {
      await review.mutateAsync({ questionId, score, reason });
      toast.show({ message: 'Score recorded.' });
    } catch {
      toast.show({ tone: 'warning', message: 'Could not record score.' });
    }
  }

  const pending = (attempt.data?.attempt.answers ?? []).filter((a) => a.score === undefined);
  return (
    <Drawer open={Boolean(attemptId)} onClose={onClose} title="Review attempt" size="lg">
      {attempt.isLoading && <div className="text-ink-tertiary">Loading…</div>}
      {attempt.data && (
        <div className="stack">
          <Notice tone="info">
            {pending.length === 0
              ? 'All answers are graded. This attempt is fully scored.'
              : `${pending.length} answer(s) need human grading. Enter a score + a one-line reason.`}
          </Notice>
          <div className="text-sm">
            Auto-total so far: <b>{attempt.data.attempt.totalScore ?? 0}</b> · Pending: <b>{attempt.data.attempt.pendingReview ?? 0}</b>
          </div>
          <ul className="grid gap-3">
            {pending.map((a) => (
              <li key={a.question} className="p-3 rounded border border-line-primary">
                <div className="text-xs text-ink-tertiary mb-1">Question · {String(a.question).slice(-6)}</div>
                <div className="p-2 rounded bg-bg-secondary/40 text-sm mb-2 whitespace-pre-wrap">{String(a.value ?? '')}</div>
                <div className="grid grid-cols-[80px_1fr_auto] gap-2 items-center">
                  <input
                    type="number" min={0} placeholder="Score"
                    value={scores[a.question] ?? ''}
                    onChange={(e) => setScores((s) => ({ ...s, [a.question]: e.target.value }))}
                    className="input"
                  />
                  <input
                    type="text" placeholder="One-line reason (required, min 5 chars)"
                    value={reasons[a.question] ?? ''}
                    onChange={(e) => setReasons((r) => ({ ...r, [a.question]: e.target.value }))}
                    className="input"
                  />
                  <Button variant="primary" onClick={() => submitScore(a.question)} loading={review.isPending}>Save</Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
}
